'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_PRODUCT_ATTRIBUTE_GROUP,
  DELETE_PRODUCT_ATTRIBUTE_GROUP,
  GET_PRODUCT_ATTRIBUTE_GROUP,
  UPDATE_PRODUCT_ATTRIBUTE_GROUP,
} from '../productAttributeGroupQueries'
import { ProductAttributeGroupFormValues } from '../types'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'

const formSchema = z.object({
  code: z.string().optional(),
  product_attribute_group_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
}) satisfies z.ZodType<ProductAttributeGroupFormValues>

interface ProductAttributeGroupFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function ProductAttributeGroupForm({
  data,
  onDirtyChange,
  onRequestClose,
}: ProductAttributeGroupFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueProductAttributeGroup: groupData } = {},
    loading: loadingData,
  } = useQuery(GET_PRODUCT_ATTRIBUTE_GROUP, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<ProductAttributeGroupFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      product_attribute_group_profiles: [],
    },
  })

  const [updateGroup] = useMutation(UPDATE_PRODUCT_ATTRIBUTE_GROUP, {
    onError: () => {
      toast.error('Failed to update attribute group')
    },
    onCompleted: () => {
      toast.success('Attribute group updated successfully')
    },
    refetchQueries: ['GET_PRODUCT_ATTRIBUTE_GROUPS', 'GetProductAttributeGroup', 'GET_ATTRIBUTES_TREE'],
  })

  const [deleteGroup] = useMutation(DELETE_PRODUCT_ATTRIBUTE_GROUP, {
    refetchQueries: ['GET_PRODUCT_ATTRIBUTE_GROUPS', 'GET_ATTRIBUTES_TREE'],
  })

  const handleDelete = () => {
    if (id) {
      const attrCount = groupData?.product_attributes?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteGroup({
              variables: { where: { id } },
            })
            toast.success('Attribute group deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete attribute group')
          }
        },
        description: attrCount
          ? `This group contains ${attrCount} attribute(s). Deleting this group will also permanently delete all its attributes and remove them from any products. This action cannot be undone.`
          : 'Are you sure you want to delete this attribute group? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const [createGroup] = useMutation(CREATE_PRODUCT_ATTRIBUTE_GROUP, {
    onError: () => {
      toast.error('Failed to create attribute group')
    },
    onCompleted: response => {
      toast.success('Attribute group created successfully')
      const newId = response.createProductAttributeGroup.id
      setId(newId)
    },
    refetchQueries: ['GET_PRODUCT_ATTRIBUTE_GROUPS', 'GET_ATTRIBUTES_TREE'],
  })

  function onSubmit(values: ProductAttributeGroupFormValues) {
    if (id) {
      updateGroup({
        variables: {
          where: { id },
          data: {
            code: values.code,
            profiles: values.product_attribute_group_profiles.map(p => ({
              id: p.id,
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    } else {
      createGroup({
        variables: {
          data: {
            code: values.code,
            profiles: values.product_attribute_group_profiles.map(p => ({
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (groupData) {
        const existingProfiles =
          groupData.product_attribute_group_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        form.reset({
          code: groupData.code ?? '',
          product_attribute_group_profiles: allProfiles,
        })
      } else {
        form.reset({
          code: '',
          product_attribute_group_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
        })
      }
    }
  }, [groupData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Attribute Group</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Group Name"
                description="This is the public display name."
                control={form.control}
                name={`product_attribute_group_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Unique identifier code for this group."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="e.g. seat-type"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`product_attribute_group_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
